import { PlaybackSessions } from '../services/playbackSessions'
import { useEffect, useRef, useCallback, useState } from 'react'
import { usePlaybackStore } from '../store'
import { ApiError, diagnosticsApi, videoApi, settingsApi, savedPlaylistApi } from '../services/api'
import { clamp } from '../utils'
import type { CloudProvider } from '../types'
import { logDiagnostic, useDiagnosticsStore } from '../diagnostics'

const SAVE_INTERVAL_MS = 5000

function bufferAhead(video: HTMLVideoElement) {
  for (let index = 0; index < video.buffered.length; index += 1) {
    if (video.buffered.start(index) <= video.currentTime && video.buffered.end(index) >= video.currentTime) {
      return Math.max(0, video.buffered.end(index) - video.currentTime)
    }
  }
  return 0
}

export function useVideoPlayer(provider: CloudProvider | null, canSavePlayback: boolean, canSavePlaylist: boolean, roomId: string | null) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const saveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const streamUrlRef = useRef<{ url: string; expiresAt: number } | null>(null)
  const [streamLoading, setStreamLoading] = useState(false)
  const [previewSource, setPreviewSource] = useState<string | null>(null)
  const lastBufferLogAtRef = useRef(0)
  const sessionsRef = useRef<PlaybackSessions | null>(null)
  if (!sessionsRef.current) sessionsRef.current = new PlaybackSessions(videoApi.recordPlay)
  const saveAllowed = useRef(canSavePlayback)
  useEffect(() => { saveAllowed.current = canSavePlayback }, [canSavePlayback])
  const saveSharedPlayback = useCallback((videoId: string, position: number) => {
    if (!saveAllowed.current) return
    void settingsApi.savePlaybackState(videoId, position).catch((error: unknown) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) saveAllowed.current = false
      logDiagnostic('WARN', 'PLAYER', 'PLAYBACK_SAVE_FAILED', 'Shared playback position could not be saved')
    })
  }, [])
  const loadStartedAtRef = useRef(0)
  const stallStartedAtRef = useRef<number | null>(null)
  const seekStartedAtRef = useRef<number | null>(null)
  const seekFromRef = useRef(0)
  const lowBufferRef = useRef(false)

  const {
    currentVideoId,
    
    
    status,
    volume,
    muted,
    currentPlaybackRole,
    bumperVolume,
    setStatus,
    setPosition,
    setDuration,
    setError,
    nextVideo,
    activeSavedPlaylistId,
    savedPlaylistRevision,
    pendingResumePosition,
    queueIds,
    playlist,
    setActiveSavedPlaylist,
    clearPendingResumePosition,
  } = usePlaybackStore()

  // ─── Load stream URL when video changes ──────────────────────────────────

  useEffect(() => {
    if (!currentVideoId || !provider) return

    const video = videoRef.current
    if (!video) return

    let cancelled = false

    const loadStream = async () => {
      const currentVideo = playlist.find((item) => item.id === currentVideoId)
      sessionsRef.current?.end()
      loadStartedAtRef.current = performance.now()
      setStreamLoading(true)
      setStatus('loading')
      logDiagnostic('INFO', 'MEDIA', 'MEDIA_LOADING', `Loading ${currentVideo?.name ?? currentVideoId}`, { videoId: currentVideoId, provider })
      useDiagnosticsStore.getState().updateSummary('player', { status: 'Loading', title: currentVideo?.name ?? currentVideoId, position: 0 })
      useDiagnosticsStore.getState().updateSummary('media', {
        resolution: currentVideo?.mediaMetadata?.width && currentVideo.mediaMetadata.height ? `${currentVideo.mediaMetadata.width}×${currentVideo.mediaMetadata.height}` : null,
        videoCodec: currentVideo?.mediaMetadata?.videoCodec ?? null,
        audioCodec: currentVideo?.mediaMetadata?.audioCodec ?? null,
        fps: currentVideo?.mediaMetadata?.frameRate ?? null,
        bitrate: currentVideo?.mediaMetadata?.bitrate ?? null,
        fileSize: currentVideo?.size ?? null,
        sourceType: currentVideo?.mimeType ?? provider,
      })

      try {
        saveSharedPlayback(currentVideoId, 0)

        const requestStartedAt = performance.now()
        const result = await videoApi.getStreamUrl(currentVideoId, provider)
        const requestDurationMs = performance.now() - requestStartedAt
        if (cancelled) return

        useDiagnosticsStore.getState().updateSummary('network', { httpStatus: 200, requestDurationMs })
        logDiagnostic('INFO', 'NETWORK', 'STREAM_URL_READY', `HTTP 200 • ${Math.round(requestDurationMs)} ms`, { requestDurationMs, videoId: currentVideoId })

        streamUrlRef.current = result
        setPreviewSource(result.url)
        video.src = result.url
        if (pendingResumePosition && pendingResumePosition > 0) {
          const resumeOnce = () => {
            video.currentTime = clamp(pendingResumePosition, 0, video.duration || 0)
            clearPendingResumePosition()
            video.removeEventListener('loadedmetadata', resumeOnce)
          }
          video.addEventListener('loadedmetadata', resumeOnce)
        }
        video.load()
        video.play().catch((err) => {
          if (!cancelled) {
            console.error('Autoplay failed:', err)
            setStatus('paused')
          }
        })
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load video'
          console.error('Stream URL error:', msg)
          void videoApi.reportError(currentVideoId, msg)
          void diagnosticsApi.logPlayback({
            level: 'error', eventName: 'stream_url_failed', videoId: currentVideoId,
            details: { message: msg.slice(0, 240), provider },
          })
          const network = useDiagnosticsStore.getState().summary.network
          useDiagnosticsStore.getState().updateSummary('network', { failedRequests: network.failedRequests + 1 })
          logDiagnostic('ERROR', 'NETWORK', 'STREAM_URL_FAILED', msg, { videoId: currentVideoId, provider })
          setError(msg)

          // Auto-skip after error
          setTimeout(() => {
            if (!cancelled) nextVideo()
          }, 3000)
        }
      } finally {
        if (!cancelled) setStreamLoading(false)
      }
    }

    void loadStream()

    return () => {
      cancelled = true
      if (video) {
        video.pause()
        video.src = ''
        video.load()
      }
      setPreviewSource(null)
    }
  }, [roomId, saveSharedPlayback, clearPendingResumePosition, currentVideoId, pendingResumePosition, playlist, provider])

  // ─── Sync volume/muted ────────────────────────────────────────────────────

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    video.volume = currentPlaybackRole === 'bumper' ? bumperVolume : volume
    video.muted = muted
    useDiagnosticsStore.getState().updateSummary('player', { volume: video.volume, muted })
  }, [bumperVolume, currentPlaybackRole, volume, muted])

  // ─── Wire video events ────────────────────────────────────────────────────

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const onPlay = () => {
      const wasBuffering = usePlaybackStore.getState().status === 'buffering'
      setStatus('playing')
      useDiagnosticsStore.getState().updateSummary('player', { status: 'Playing' })
      if (currentVideoId && provider) {
        void sessionsRef.current?.play(currentVideoId, provider, roomId).catch(() => {
          logDiagnostic('WARN', 'PLAYER', 'VIEWING_LOG_FAILED', 'Viewing event could not be recorded after retries')
        })
      }
      void diagnosticsApi.logPlayback({
        level: 'info', eventName: wasBuffering ? 'playback_recovered' : 'playback_started',
        videoId: currentVideoId, details: { positionSeconds: Math.floor(video.currentTime) },
      })
      logDiagnostic('INFO', 'PLAYER', wasBuffering ? 'PLAYBACK_RECOVERED' : 'PLAYBACK_STARTED', wasBuffering ? 'Playback recovered' : 'Playback started', { position: video.currentTime })
    }
    const onPause = () => {
      if (video.ended) return
      setStatus('paused')
      useDiagnosticsStore.getState().updateSummary('player', { status: 'Paused' })
      logDiagnostic('INFO', 'PLAYER', 'PLAYBACK_PAUSED', 'Playback paused', { position: video.currentTime })
    }
    const onWaiting = () => {
      setStatus('buffering')
      if (stallStartedAtRef.current === null) {
        stallStartedAtRef.current = performance.now()
        const performanceSummary = useDiagnosticsStore.getState().summary.performance
        useDiagnosticsStore.getState().updateSummary('performance', { stalls: performanceSummary.stalls + 1 })
      }
      useDiagnosticsStore.getState().updateSummary('player', { status: 'Buffering' })
      if (Date.now() - lastBufferLogAtRef.current > 10_000) {
        lastBufferLogAtRef.current = Date.now()
        void diagnosticsApi.logPlayback({
          level: 'warn', eventName: 'playback_buffering', videoId: currentVideoId,
          details: { positionSeconds: Math.floor(video.currentTime), readyState: video.readyState, networkState: video.networkState },
        })
        logDiagnostic('WARN', 'BUFFER', 'PLAYBACK_BUFFERING', 'Playback buffering', { position: video.currentTime, readyState: video.readyState, networkState: video.networkState })
      }
    }
    const onCanPlay = () => {
      const currentStatus = usePlaybackStore.getState().status
      if (currentStatus === 'buffering' || currentStatus === 'loading') {
        setStatus('playing')
        useDiagnosticsStore.getState().updateSummary('player', { status: 'Playing' })
      }
      if (stallStartedAtRef.current !== null) {
        const duration = performance.now() - stallStartedAtRef.current
        stallStartedAtRef.current = null
        const total = useDiagnosticsStore.getState().summary.performance.totalStallDurationMs + duration
        useDiagnosticsStore.getState().updateSummary('performance', { totalStallDurationMs: total })
        logDiagnostic('INFO', 'BUFFER', 'BUFFER_RECOVERED', `Recovered • ${(bufferAhead(video)).toFixed(1)} sec buffered`, { stallDurationMs: duration })
      }
    }
    const onTimeUpdate = () => setPosition(video.currentTime)
    const onDurationChange = () => {
      if (video.duration && !isNaN(video.duration)) {
        setDuration(video.duration)
        useDiagnosticsStore.getState().updateSummary('player', { duration: video.duration })
      }
    }
    const onEnded = () => {
      sessionsRef.current?.end()
      useDiagnosticsStore.getState().updateSummary('player', { status: 'Ended', position: video.duration })
      logDiagnostic('INFO', 'PLAYER', 'PLAYBACK_ENDED', 'Playback ended', { duration: video.duration })
      setStatus('idle')
      nextVideo()
      const completed = usePlaybackStore.getState()
      if (canSavePlaylist && completed.activeSavedPlaylistId && completed.currentVideoId === null) {
        void savedPlaylistApi.saveResume(completed.activeSavedPlaylistId, {
          videoId: null,
          position: 0,
          queueIds: [],
          completed: true,
          revision: completed.savedPlaylistRevision,
        }).then((saved) => {
          setActiveSavedPlaylist(saved.id, saved.resume.revision)
        }).catch(() => {
          // Another session completed or advanced this playlist first.
        })
      }
    }
    const onError = () => {
      const err = video.error
      // Ignore spurious "Empty src" errors from cleanup / src reset.
      if (!video.src || video.src === window.location.href || (err?.message ?? '').includes('Empty src')) {
        return
      }
      const msg = err ? `Video error ${err.code}: ${err.message}` : 'Playback error'
      console.error(msg, err)
      if (currentVideoId) void videoApi.reportError(currentVideoId, msg)
      void diagnosticsApi.logPlayback({
        level: 'error', eventName: 'playback_error', videoId: currentVideoId,
        details: { code: err?.code ?? null, message: (err?.message || 'Playback error').slice(0, 240), readyState: video.readyState, networkState: video.networkState },
      })
      useDiagnosticsStore.getState().updateSummary('player', { status: 'Error' })
      logDiagnostic('ERROR', 'ERROR', 'PLAYBACK_ERROR', msg, { code: err?.code ?? null, readyState: video.readyState, networkState: video.networkState })
      setError(msg)
      setTimeout(nextVideo, 3000)
    }
    const onLoadedMetadata = () => {
      const resolution = video.videoWidth && video.videoHeight ? `${video.videoWidth}×${video.videoHeight}` : null
      useDiagnosticsStore.getState().updateSummary('media', { resolution })
      logDiagnostic('INFO', 'MEDIA', 'METADATA_LOADED', `Metadata loaded${resolution ? ` • ${resolution}` : ''}`, { width: video.videoWidth, height: video.videoHeight, duration: video.duration })
    }
    const onLoadedData = () => {
      const startupTimeMs = loadStartedAtRef.current ? performance.now() - loadStartedAtRef.current : null
      useDiagnosticsStore.getState().updateSummary('performance', { startupTimeMs })
      logDiagnostic('INFO', 'VIDEO', 'FIRST_FRAME', `First frame rendered${startupTimeMs === null ? '' : ` • ${Math.round(startupTimeMs)} ms`}`, { startupTimeMs })
    }
    const onSeeking = () => { seekStartedAtRef.current = performance.now(); seekFromRef.current = video.currentTime }
    const onSeeked = () => {
      const duration = seekStartedAtRef.current === null ? null : performance.now() - seekStartedAtRef.current
      seekStartedAtRef.current = null
      useDiagnosticsStore.getState().updateSummary('performance', { lastSeekDurationMs: duration })
      logDiagnostic('INFO', 'PLAYER', 'SEEK_COMPLETED', `Seek completed${duration === null ? '' : ` • ${Math.round(duration)} ms`}`, { from: seekFromRef.current, to: video.currentTime, durationMs: duration })
    }
    const onStalled = () => {
      if (stallStartedAtRef.current === null) {
        stallStartedAtRef.current = performance.now()
        const performanceSummary = useDiagnosticsStore.getState().summary.performance
        useDiagnosticsStore.getState().updateSummary('performance', { stalls: performanceSummary.stalls + 1 })
      }
      useDiagnosticsStore.getState().updateSummary('player', { status: 'Stalled' })
      logDiagnostic('WARN', 'NETWORK', 'MEDIA_STALLED', 'Media download stalled', { position: video.currentTime })
    }
    const onRateChange = () => useDiagnosticsStore.getState().updateSummary('player', { playbackRate: video.playbackRate })
    const onEnterPictureInPicture = () => useDiagnosticsStore.getState().updateSummary('player', { pictureInPicture: true })
    const onLeavePictureInPicture = () => useDiagnosticsStore.getState().updateSummary('player', { pictureInPicture: false })

    video.addEventListener('play', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('waiting', onWaiting)
    video.addEventListener('canplay', onCanPlay)
    video.addEventListener('timeupdate', onTimeUpdate)
    video.addEventListener('durationchange', onDurationChange)
    video.addEventListener('ended', onEnded)
    video.addEventListener('error', onError)
    video.addEventListener('loadedmetadata', onLoadedMetadata)
    video.addEventListener('loadeddata', onLoadedData)
    video.addEventListener('seeking', onSeeking)
    video.addEventListener('seeked', onSeeked)
    video.addEventListener('stalled', onStalled)
    video.addEventListener('ratechange', onRateChange)
    video.addEventListener('enterpictureinpicture', onEnterPictureInPicture)
    video.addEventListener('leavepictureinpicture', onLeavePictureInPicture)

    return () => {
      video.removeEventListener('play', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('waiting', onWaiting)
      video.removeEventListener('canplay', onCanPlay)
      video.removeEventListener('timeupdate', onTimeUpdate)
      video.removeEventListener('durationchange', onDurationChange)
      video.removeEventListener('ended', onEnded)
      video.removeEventListener('error', onError)
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('loadeddata', onLoadedData)
      video.removeEventListener('seeking', onSeeking)
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('stalled', onStalled)
      video.removeEventListener('ratechange', onRateChange)
      video.removeEventListener('enterpictureinpicture', onEnterPictureInPicture)
      video.removeEventListener('leavepictureinpicture', onLeavePictureInPicture)
    }
  }, [roomId, canSavePlaylist, currentVideoId, nextVideo, provider, setActiveSavedPlaylist, setDuration, setError, setPosition, setStatus])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const update = () => {
      const ahead = bufferAhead(video)
      const quality = typeof video.getVideoPlaybackQuality === 'function' ? video.getVideoPlaybackQuality() : null
      const health = ahead < 2 ? 'Low' : ahead < 8 ? 'Fair' : 'Healthy'
      useDiagnosticsStore.getState().updateSummary('player', {
        position: video.currentTime, duration: Number.isFinite(video.duration) ? video.duration : 0,
        playbackRate: video.playbackRate, fullscreen: Boolean(document.fullscreenElement),
        pictureInPicture: document.pictureInPictureElement === video,
      })
      useDiagnosticsStore.getState().updateSummary('media', { bufferedSeconds: ahead })
      useDiagnosticsStore.getState().updateSummary('performance', {
        droppedFrames: quality?.droppedVideoFrames ?? 0, totalFrames: quality?.totalVideoFrames ?? 0, bufferHealth: health,
      })
      const rect = video.getBoundingClientRect()
      useDiagnosticsStore.getState().updateSummary('system', { playerViewport: `${Math.round(rect.width)}×${Math.round(rect.height)}` })
      if (!video.paused && ahead < 2 && !lowBufferRef.current) {
        lowBufferRef.current = true
        logDiagnostic('WARN', 'BUFFER', 'BUFFER_LOW', `Buffer low • ${ahead.toFixed(1)} sec remaining`, { bufferAhead: ahead, currentTime: video.currentTime })
      } else if (ahead >= 8 && lowBufferRef.current) {
        lowBufferRef.current = false
        logDiagnostic('INFO', 'BUFFER', 'BUFFER_HEALTHY', `Recovered • ${ahead.toFixed(1)} sec buffered`, { bufferAhead: ahead, currentTime: video.currentTime })
      }
    }
    const timer = window.setInterval(update, 1000)
    update()
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    const updateNetwork = () => {
      useDiagnosticsStore.getState().updateSummary('network', { online: navigator.onLine })
      logDiagnostic(navigator.onLine ? 'INFO' : 'ERROR', 'NETWORK', navigator.onLine ? 'NETWORK_ONLINE' : 'NETWORK_OFFLINE', navigator.onLine ? 'Network connection restored' : 'Browser is offline')
    }
    window.addEventListener('online', updateNetwork)
    window.addEventListener('offline', updateNetwork)
    return () => { window.removeEventListener('online', updateNetwork); window.removeEventListener('offline', updateNetwork) }
  }, [])

  // ─── Periodic state save ──────────────────────────────────────────────────

  useEffect(() => {
    if (saveTimerRef.current) clearInterval(saveTimerRef.current)

    if (canSavePlayback && status === 'playing' && currentVideoId) {
      saveTimerRef.current = setInterval(() => {
        const pos = videoRef.current?.currentTime ?? 0
        saveSharedPlayback(currentVideoId, pos)
        if (canSavePlaylist && activeSavedPlaylistId) {
          void savedPlaylistApi.saveResume(activeSavedPlaylistId, {
            videoId: currentVideoId,
            position: pos,
            queueIds,
            completed: false,
            revision: savedPlaylistRevision,
          }).then((saved) => {
            setActiveSavedPlaylist(saved.id, saved.resume.revision)
          }).catch(() => {
            // A newer client state wins instead of being overwritten.
          })
        }
      }, SAVE_INTERVAL_MS)
    }

    return () => {
      if (saveTimerRef.current) clearInterval(saveTimerRef.current)
    }
  }, [saveSharedPlayback, canSavePlaylist, activeSavedPlaylistId, canSavePlayback, currentVideoId, queueIds, savedPlaylistRevision, setActiveSavedPlaylist, status])

  // ─── Controls ─────────────────────────────────────────────────────────────

  const play = useCallback(() => {
    videoRef.current?.play()
  }, [])

  const pause = useCallback(() => {
    videoRef.current?.pause()
  }, [])

  const togglePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) video.play()
    else video.pause()
  }, [])

  const seek = useCallback((seconds: number) => {
    const video = videoRef.current
    if (!video) return
    video.currentTime = clamp(seconds, 0, video.duration || 0)
  }, [])

  const seekRelative = useCallback((delta: number) => {
    const video = videoRef.current
    if (!video) return
    video.currentTime = clamp(
      video.currentTime + delta,
      0,
      video.duration || 0,
    )
  }, [])

  return {
    videoRef,
    streamLoading,
    play,
    pause,
    togglePlay,
    seek,
    seekRelative,
    previewSource,
  }
}
